import React, { useCallback, useEffect, useState } from 'react';
import { Select, Alert, Tabs, Button } from 'antd';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  fetchCharityDonations,
  fetchCharityDonationsSummary,
  updateCharityDonationStatus,
  updateCharityDonationEntriesBulk,
} from '../../api/charity-api';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import { formatCharityDonationStatusAsPill } from '../../utils/formatCharityDonationStatus';
import GenericTable from '../tables/GenericTable';
import Spinner from '../elements/Spinner/Spinner';
import Pagination from '../layout/Pagination';
import BulkMarkPaidConfirmModal from '../modals/BulkMarkPaidConfirmModal';

const ORDER_TYPE_LABELS = {
  standard: 'Standardna',
  mentor: 'Preko mentora',
  referral: 'Preko preporuke',
};

const orderTypeOptions = [
  { value: null, label: 'Sve narudžbine' },
  { value: 'standard', label: ORDER_TYPE_LABELS.standard },
  { value: 'mentor', label: ORDER_TYPE_LABELS.mentor },
  { value: 'referral', label: ORDER_TYPE_LABELS.referral },
];

// 'all' isn't a real backend status — it just means "don't filter by status"
// when passed through to fetchCharityDonations.
const statusTabItems = [
  { key: 'all', label: 'Sve' },
  { key: 'pending', label: 'Na čekanju' },
  { key: 'paid', label: 'Realizovano' },
];

const isRowMarkable = (row) => row.status === 'pending';

const summaryCardStyle = {
  border: '1px solid #E5E7EB',
  borderRadius: 12,
  padding: '20px 24px',
  minWidth: 200,
};

export default function CharityEarnings() {
  const [orderType, setOrderType] = useState(null);
  const [statusTab, setStatusTab] = useState('all');
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const status = statusTab === 'all' ? null : statusTab;
    const [summaryResult, listResult] = await Promise.all([
      fetchCharityDonationsSummary({ orderType }),
      fetchCharityDonations({
        orderType,
        status,
        page: currentPage,
        limit: 10,
      }),
    ]);
    if (summaryResult.error || listResult.error) {
      setError(summaryResult.error || listResult.error);
      setSummary(null);
      setItems([]);
    } else {
      setError('');
      setSummary(summaryResult.data);
      setItems(listResult.data.items || []);
      setTotalPages(listResult.data.totalPages || 1);
    }
    setSelectedRowKeys([]);
    setLoading(false);
  }, [orderType, statusTab, currentPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOrderTypeChange = (val) => {
    setOrderType(val);
    setCurrentPage(1);
  };

  const handleStatusTabChange = (key) => {
    setStatusTab(key);
    setCurrentPage(1);
  };

  const handleMarkEntryStatus = async (row, status) => {
    const { error: updateError } = await updateCharityDonationStatus(
      row._id,
      status
    );
    if (updateError) {
      toast.error(updateError);
      return;
    }
    toast.success('Status donacije je ažuriran.');
    loadData();
  };

  const handleBulkMarkPaid = async () => {
    const entryIds = items
      .filter((row) => selectedRowKeys.includes(row._id) && isRowMarkable(row))
      .map((row) => row._id);

    if (entryIds.length === 0) {
      toast.error('Nijedna od izabranih donacija ne čeka realizaciju.');
      return;
    }

    setBulkUpdating(true);
    const { error: bulkError } = await updateCharityDonationEntriesBulk(
      entryIds,
      'paid'
    );
    setBulkUpdating(false);
    if (bulkError) {
      toast.error(bulkError);
      return;
    }
    toast.success('Donacije su označene kao realizovane.');
    setBulkConfirmOpen(false);
    loadData();
  };

  const selectedMarkableRows = items.filter(
    (row) => selectedRowKeys.includes(row._id) && isRowMarkable(row)
  );
  const selectedMarkableTotal = selectedMarkableRows.reduce(
    (sum, row) => sum + (row.amount || 0),
    0
  );

  const columns = [
    {
      key: 'orderDate',
      label: 'Datum narudžbine',
      render: (val) => (val ? dayjs(val).format('DD.MM.YYYY') : '-'),
    },
    { key: 'orderNumber', label: 'Broj narudžbine' },
    {
      key: 'orderType',
      label: 'Tip narudžbine',
      render: (val) => ORDER_TYPE_LABELS[val] || val,
    },
    {
      key: 'orderTotal',
      label: 'Vrednost narudžbine',
      render: (val) => formatPriceWithCurrency(val || 0),
    },
    {
      key: 'amount',
      label: 'Donacija (0.5%)',
      render: (val) => formatPriceWithCurrency(val || 0),
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => formatCharityDonationStatusAsPill(val),
    },
  ];

  const actions = [
    {
      key: 'mark-paid',
      label: 'Označi kao realizovano',
      visible: (row) => isRowMarkable(row),
      onClick: (row) => handleMarkEntryStatus(row, 'paid'),
    },
    {
      key: 'mark-pending',
      label: 'Označi kao nerealizovano',
      visible: (row) => row.status === 'paid',
      onClick: (row) => handleMarkEntryStatus(row, 'pending'),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
          marginTop: 12,
          marginBottom: 12,
        }}
      >
        <p className="title" style={{ margin: 0 }}>
          Humanitarne donacije — Second Chance for Children
        </p>
        <Select
          value={orderType}
          options={orderTypeOptions}
          onChange={handleOrderTypeChange}
          style={{ minWidth: 200 }}
        />
      </div>

      <Tabs
        activeKey={statusTab}
        onChange={handleStatusTabChange}
        items={statusTabItems}
        className="medics_tab"
        style={{ marginBottom: 20 }}
      />

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {loading ? (
        <Spinner size="lg" />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              marginBottom: 24,
            }}
          >
            <div style={summaryCardStyle}>
              <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
                Ukupno donirano
              </div>
              <div style={{ fontSize: 24, fontWeight: 600 }}>
                {formatPriceWithCurrency(summary?.totalAmount || 0)}
              </div>
            </div>
            <div style={summaryCardStyle}>
              <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
                Realizovano humanitarnoj organizaciji
              </div>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#027A48' }}>
                {formatPriceWithCurrency(summary?.totalPaidAmount || 0)}
              </div>
            </div>
            <div style={summaryCardStyle}>
              <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
                Na čekanju
              </div>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#DC6803' }}>
                {formatPriceWithCurrency(summary?.totalPendingAmount || 0)}
              </div>
            </div>
            <div style={summaryCardStyle}>
              <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
                Ukupna vrednost narudžbina
              </div>
              <div style={{ fontSize: 24, fontWeight: 600 }}>
                {formatPriceWithCurrency(summary?.totalOrderValue || 0)}
              </div>
            </div>
            <div style={summaryCardStyle}>
              <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
                Broj narudžbina
              </div>
              <div style={{ fontSize: 24, fontWeight: 600 }}>
                {summary?.totalOrders || 0}
              </div>
            </div>
          </div>

          {selectedMarkableRows.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Button
                className="btn-primary"
                onClick={() => setBulkConfirmOpen(true)}
              >
                Označi {selectedMarkableRows.length} kao realizovano
              </Button>
            </div>
          )}

          <div className="products_wrapper">
            <GenericTable
              data={items}
              columns={columns}
              actions={actions}
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
                getCheckboxProps: (row) => ({ disabled: !isRowMarkable(row) }),
              }}
              emptyState={{
                icon: '/assets/icons/no-ads.svg',
                title: 'Nema podataka o donacijama',
              }}
            />
          </div>

          <div style={{ marginTop: 16, marginBottom: 24 }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>

          <p style={{ color: '#6B7280', fontSize: 13, marginTop: 16 }}>
            0.5% od vrednosti svake naplaćene narudžbine izdvaja se za
            humanitarnu organizaciju „Second Chance for Children“. Otkazane i
            vraćene narudžbine nisu uračunate.
          </p>
        </>
      )}

      <BulkMarkPaidConfirmModal
        visible={bulkConfirmOpen}
        onClose={() => setBulkConfirmOpen(false)}
        onConfirm={handleBulkMarkPaid}
        count={selectedMarkableRows.length}
        totalAmount={selectedMarkableTotal}
        loading={bulkUpdating}
        title="Potvrda isplate donacije"
        itemSingular="donaciju"
        itemPlural="donacije"
      />
    </div>
  );
}
